import { supabase } from './supabase'
import { loadPostBylines } from './feed'

/**
 * 145 (FED-48), 146. The photo room.
 *
 * Feed photos entered in the photo contest and cleared for marketing —
 * app.photo_may_feature: the uploader said yes (141) and nobody tagged in it
 * said no (143) — on live posts the viewer can see. Only `photo.room` holders;
 * every function here REFUSES anybody else (rule 12), so the page's own check
 * is a courtesy.
 *
 * 146 added: the full-size copy and where a photo came from (photo_source);
 * a vocabulary of equipment, sector and feature values that OneConetec
 * suggests and people accept or reject; a searchable description; a
 * plain-language search the photo-room Edge Function scores; and contests.
 *
 * ── A DOWNLOAD IS A RECORD, THEN A FILE ─────────────────────────────────────
 * `downloadPhoto` calls record_photo_download FIRST and signs the URL only
 * when that returns. If the record is refused (the box was unticked after the
 * page loaded), no file is fetched. photo.used is OFF since 146 (David, 10
 * Oct), so a download normally pays nothing and says nothing about stars.
 */

/* 302 (FED-70): brand joins, first - the livery is what people look for. */
export type Facet = 'brand' | 'equipment' | 'sector' | 'feature'
export const FACETS: readonly Facet[] = ['brand', 'equipment', 'sector', 'feature']

export type VocabValue = {
  id: string
  tenantId: string
  facet: Facet
  key: string
  labelEn: string
  labelEs: string
  retired: boolean
}

export type RoomFeature = {
  valueId: string
  facet: Facet
  key: string
  labelEn: string
  labelEs: string
  state: 'suggested' | 'accepted'
  confidence: number | null
}

export type RoomPerson = { personId: string; name: string }
export type RoomDownload = {
  personId: string
  name: string
  at: string
  variant: 'feed' | 'original'
  /** 148. What it was taken for. Null on downloads from before 148. */
  use: PhotoUse | null
}

/** 148 (FED-53). The three uses a room download can be for. Contests are not downloads. */
export type PhotoUse = 'marketing' | 'sales' | 'other'
export const PHOTO_USES: PhotoUse[] = ['marketing', 'sales', 'other']
export type RoomWin = { contestId: string; title: string; place: number }

export type RoomPhoto = {
  path: string
  contentId: string
  tenantId: string
  caption: string | null
  mimeType: string
  width: number | null
  height: number | null
  byteSize: number | null
  postedAt: string
  uploaderId: string
  uploaderName: string
  /** 300. The byline the photo's post went out under ("Communications"), or null. */
  bylineName: string | null
  uploaderBu: string | null
  uploaderCountry: string | null
  people: RoomPerson[]
  topics: string[]
  descriptionEn: string | null
  descriptionEs: string | null
  descriptionBy: 'oneconetec' | 'person' | null
  features: RoomFeature[]
  /** "2026-10-01T07:12:00" — the camera's wall clock. */
  takenAtLocal: string | null
  takenOffset: string | null
  camera: string | null
  originalWidth: number | null
  originalHeight: number | null
  originalBytes: number | null
  downloadCount: number
  downloads: RoomDownload[]
  starsPaid: boolean
  wins: RoomWin[]
  /** 304 (FED-71). A photo added straight to the Gallery by a Gallery admin —
   *  never a feed post. Null for a feed photo. From gallery_uploads(), beside
   *  photo_room_photos (its rows cannot grow a column). */
  gallery: GalleryUpload | null
}

export type TakenPrecision = 'minute' | 'day' | 'month' | 'year'
export type GalleryUpload = {
  /** The file's name as picked — Gallery admins only, null for everyone else. */
  sourceName: string | null
  /** How much of takenAtLocal was given (set_photo_taken); null when none was recorded. */
  takenPrecision: TakenPrecision | null
}

export type RoomSort = 'posted' | 'taken' | 'downloads' | 'winners' | 'resolution'

export type RoomQuery = {
  values?: string[]
  from?: string | null
  to?: string | null
  tz?: string | null
  sort?: RoomSort
  /** 318: only these photos — a pin or cluster chosen on the map. The database filters. */
  paths?: string[] | null
}

export type RoomPage = { photos: RoomPhoto[]; total: number }

/** The page size. The limit is applied by the database (standing rule 1). */
export const ROOM_PAGE = 48

export function toRoomPhoto(r: Record<string, unknown>): RoomPhoto {
  return {
    path: r.storage_path as string,
    contentId: r.content_id as string,
    tenantId: r.tenant_id as string,
    caption: (r.caption as string | null) ?? null,
    mimeType: (r.mime_type as string) ?? 'image/jpeg',
    width: (r.width as number | null) ?? null,
    height: (r.height as number | null) ?? null,
    byteSize: (r.byte_size as number | null) ?? null,
    postedAt: r.posted_at as string,
    uploaderId: r.uploader_id as string,
    uploaderName: r.uploader_name as string,
    bylineName: null,
    uploaderBu: (r.uploader_bu as string | null) ?? null,
    uploaderCountry: (r.uploader_country as string | null) ?? null,
    people: ((r.people as { person_id: string; name: string }[] | null) ?? []).map((p) => ({
      personId: p.person_id,
      name: p.name,
    })),
    topics: (r.topics as string[] | null) ?? [],
    descriptionEn: (r.description_en as string | null) ?? null,
    descriptionEs: (r.description_es as string | null) ?? null,
    descriptionBy: (r.description_by as 'oneconetec' | 'person' | null) ?? null,
    features: (
      (r.features as {
        value_id: string; facet: Facet; key: string; label_en: string; label_es: string
        state: 'suggested' | 'accepted'; confidence: number | null
      }[] | null) ?? []
    ).map((f) => ({
      valueId: f.value_id,
      facet: f.facet,
      key: f.key,
      labelEn: f.label_en,
      labelEs: f.label_es,
      state: f.state,
      confidence: f.confidence === null || f.confidence === undefined ? null : Number(f.confidence),
    })),
    takenAtLocal: (r.taken_at_local as string | null) ?? null,
    takenOffset: (r.taken_offset as string | null) ?? null,
    camera: (r.camera as string | null) ?? null,
    originalWidth: (r.original_width as number | null) ?? null,
    originalHeight: (r.original_height as number | null) ?? null,
    originalBytes: (r.original_bytes as number | null) ?? null,
    downloadCount: Number(r.download_count ?? 0),
    downloads: (
      (r.downloads as { person_id: string; name: string; at: string; variant?: 'feed' | 'original'; use?: PhotoUse | null }[] | null) ?? []
    ).map((d) => ({ personId: d.person_id, name: d.name, at: d.at, variant: d.variant ?? 'feed', use: d.use ?? null })),
    starsPaid: r.stars_paid === true,
    wins: ((r.wins as { contest_id: string; title: string; place: number }[] | null) ?? []).map((w) => ({
      contestId: w.contest_id,
      title: w.title,
      place: w.place,
    })),
    gallery: null,
  }
}

export async function loadPhotoRoom(q: RoomQuery = {}, offset = 0, limit = ROOM_PAGE): Promise<RoomPage> {
  const { data, error } = await supabase.rpc('photo_room_photos', {
    p_query: null,
    p_values: q.values && q.values.length ? q.values : null,
    p_from: q.from ?? null,
    p_to: q.to ?? null,
    p_tz: q.tz ?? null,
    p_sort: q.sort ?? 'posted',
    p_limit: limit,
    p_offset: offset,
    p_paths: q.paths && q.paths.length ? q.paths : null,
  })
  if (error) throw error
  const rows = (data ?? []) as Record<string, unknown>[]
  return { total: rows.length ? Number(rows[0].total_count) : 0, photos: await withGalleryUploads(await withPhotoBylines(rows.map(toRoomPhoto))) }
}

/** 300. The photo room's rows cannot grow a column (no top-level DROP), so
 *  the bylines of the posts on the page come from post_bylines(). */
export async function withPhotoBylines<T extends { contentId: string; bylineName: string | null }>(photos: T[]): Promise<T[]> {
  const by = await loadPostBylines(photos.map((p) => p.contentId))
  if (by.size === 0) return photos
  return photos.map((p) => (by.has(p.contentId) ? { ...p, bylineName: by.get(p.contentId)!.name } : p))
}

/** 304. The same shape for Gallery uploads: which of the page's posts were
 *  added straight to the Gallery, from gallery_uploads(). */
export async function withGalleryUploads<T extends { contentId: string; gallery: GalleryUpload | null }>(photos: T[]): Promise<T[]> {
  if (photos.length === 0) return photos
  const ids = Array.from(new Set(photos.map((p) => p.contentId)))
  const found = new Map<string, GalleryUpload>()
  for (let i = 0; i < ids.length; i += 200) {
    const { data, error } = await supabase.rpc('gallery_uploads', { p_content_ids: ids.slice(i, i + 200) })
    if (error) throw error
    for (const r of (data as { content_id: string; source_name: string | null; taken_precision: string | null }[] | null) ?? []) {
      found.set(r.content_id, { sourceName: r.source_name ?? null, takenPrecision: (r.taken_precision as TakenPrecision | null) ?? null })
    }
  }
  if (found.size === 0) return photos
  return photos.map((p) => (found.has(p.contentId) ? { ...p, gallery: found.get(p.contentId)! } : p))
}

/* ── 305 (FED-72): everyone says what is in a photo, and why ──────────────── */
export type CrowdAnswer = { name: string; yes: boolean; reason: string | null; decides: boolean; at: string }
export type CrowdFeature = Omit<RoomFeature, 'state'> & {
  state: 'suggested' | 'accepted' | 'rejected'
  /** The colleague who added it when One had not suggested it; null = One. */
  suggestedBy: string | null
  myAnswer: boolean | null
  yes: number
  no: number
  /** Every answer with its name and reason — the poster and the Gallery roles only; null for everyone else. */
  answers: CrowdAnswer[] | null
}
export type PhotoFeatures = { described: boolean; decides: boolean; features: CrowdFeature[] }

/** The tags on photos the caller can see in the Gallery, with their own answer and the counts. */
export async function photoFeaturesFor(paths: string[]): Promise<Map<string, PhotoFeatures>> {
  const out = new Map<string, PhotoFeatures>()
  if (paths.length === 0) return out
  const { data, error } = await supabase.rpc('photo_features_for', { p_paths: paths.slice(0, 20) })
  if (error) throw error
  for (const r of (data as { storage_path: string; described: boolean; decides: boolean; features: unknown[] }[] | null) ?? []) {
    out.set(r.storage_path, {
      described: r.described === true,
      decides: r.decides === true,
      features: ((r.features ?? []) as Record<string, unknown>[]).map((f) => ({
        valueId: f.value_id as string,
        facet: f.facet as Facet,
        key: f.key as string,
        labelEn: f.label_en as string,
        labelEs: f.label_es as string,
        state: f.state as 'suggested' | 'accepted' | 'rejected',
        confidence: f.confidence === null || f.confidence === undefined ? null : Number(f.confidence),
        suggestedBy: (f.suggested_by as string | null) ?? null,
        myAnswer: f.my_answer === null || f.my_answer === undefined ? null : f.my_answer === true,
        yes: Number(f.yes ?? 0),
        no: Number(f.no ?? 0),
        answers: Array.isArray(f.answers)
          ? (f.answers as Record<string, unknown>[]).map((a) => ({
              name: a.name as string,
              yes: a.yes === true,
              reason: (a.reason as string | null) ?? null,
              decides: a.decides === true,
              at: a.at as string,
            }))
          : null,
      })),
    })
  }
  return out
}

/** Yes or no to a tag on a photo you can see, with an optional reason (280). A yes on a tag not on the photo adds it as your suggestion. */
export async function answerPhotoFeature(path: string, valueId: string, yes: boolean, reason: string | null): Promise<'suggested' | 'accepted' | 'rejected'> {
  const { data, error } = await supabase.rpc('answer_photo_feature', { p_storage_path: path, p_value_id: valueId, p_yes: yes, p_reason: reason })
  if (error) throw error
  return data as 'suggested' | 'accepted' | 'rejected'
}

/** Per tag, the reasons One is told (the last three that agree with where the tag ended). Gallery roles. */
export async function loadTagNotes(tenantId: string): Promise<Map<string, string[]>> {
  const { data, error } = await supabase.rpc('photo_describe_notes', { p_tenant: tenantId })
  if (error) throw error
  const out = new Map<string, string[]>()
  for (const r of (data as { value_id: string; notes: string[] | null }[] | null) ?? []) out.set(r.value_id, r.notes ?? [])
  return out
}

/** 304c. How many photos in the Gallery (as the caller sees it) One has not read yet. */
export async function galleryUnreadCount(): Promise<number> {
  const { data, error } = await supabase.rpc('gallery_unread_count')
  if (error) throw error
  return Number(data ?? 0)
}

/** 304. A Gallery admin takes a Gallery upload out (never a feed post — the server refuses). */
export async function removeGalleryPhoto(contentId: string, reason: string | null = null): Promise<void> {
  const { error } = await supabase.rpc('gallery_remove_photo', { p_content_id: contentId, p_reason: reason })
  if (error) throw error
}

/* ── search (photo-room Edge Function) ─────────────────────────────────────── */

export type SearchResult = {
  photos: RoomPhoto[]
  scores: Record<string, { score: number; why: string }>
  /** How many photos matched any word of the search. */
  total: number
  /** How many of those were scored (the top 60). */
  scored: number
  /** 'text' when the model could not score and the text rank stood in. */
  scoredBy: 'oneconetec' | 'text'
}

export async function searchPhotoRoom(query: string, q: RoomQuery = {}): Promise<SearchResult> {
  const { data, error } = await supabase.functions.invoke('photo-room', {
    body: { kind: 'search', query, values: q.values ?? null, from: q.from ?? null, to: q.to ?? null, tz: q.tz ?? null },
  })
  if (error) throw await readFunctionError(error)
  const d = (data ?? {}) as {
    rows?: Record<string, unknown>[]
    scores?: Record<string, { score: number; why: string }>
    total?: number
    scored?: number
    scoredBy?: 'oneconetec' | 'text'
  }
  return {
    photos: await withGalleryUploads(await withPhotoBylines((d.rows ?? []).map(toRoomPhoto))),
    scores: d.scores ?? {},
    total: d.total ?? 0,
    scored: d.scored ?? 0,
    scoredBy: d.scoredBy ?? 'oneconetec',
  }
}

/** Ask OneConetec to look at photos that have no description (or these, again). */
export async function describePhotos(paths?: string[], force = false): Promise<{ path: string; ok: boolean }[]> {
  const { data, error } = await supabase.functions.invoke('photo-room', {
    body: { kind: 'describe', paths: paths ?? null, force },
  })
  if (error) throw await readFunctionError(error)
  return ((data as { described?: { path: string; ok: boolean }[] } | null)?.described ?? [])
}

/** 149. Ask OneConetec to name the place of photos that have coordinates and no place yet. */
export async function placePhotos(): Promise<{ path: string; ok: boolean }[]> {
  const { data, error } = await supabase.functions.invoke('photo-room', { body: { kind: 'place' } })
  if (error) throw await readFunctionError(error)
  return ((data as { placed?: { path: string; ok: boolean }[] } | null)?.placed ?? [])
}

/**
 * 322. Name the place of EVERY photo that needs it, three at a time, until a
 * call names nothing (none left, or none One could name - the same photos
 * would come back). David, 30 Oct: "get the site in for the existing photos";
 * at three per Gallery visit, prod's 60 would have taken twenty visits.
 * `stop` is read between calls (the page has gone). Returns how many it named.
 */
export async function placeAllPhotos(stop: () => boolean = () => false, maxCalls = 40): Promise<number> {
  let named = 0
  for (let i = 0; i < maxCalls && !stop(); i++) {
    const r = await placePhotos()
    const ok = r.filter((d) => d.ok).length
    if (r.length === 0 || ok === 0) break
    named += ok
  }
  return named
}

/* ── 148: what each photo may be used for ─────────────────────────────────── */

export type RoomUses = {
  contest: boolean
  marketing: boolean
  sales: boolean
  other: boolean
  /** A tagged colleague said no: out of every use but contests (143/147). */
  taggedNo: boolean
  /** The poster's reason, for photo room admins (photo.location) only. */
  reason: string | null
}

export async function photoRoomUses(paths: string[]): Promise<Map<string, RoomUses>> {
  const out = new Map<string, RoomUses>()
  if (paths.length === 0) return out
  const { data, error } = await supabase.rpc('photo_room_uses', { p_paths: paths })
  if (error) throw error
  for (const r of (data ?? []) as Record<string, unknown>[]) {
    out.set(r.storage_path as string, {
      contest: r.contest === true,
      marketing: r.marketing === true,
      sales: r.sales === true,
      other: r.other === true,
      taggedNo: r.tagged_no === true,
      reason: (r.reason as string | null) ?? null,
    })
  }
  return out
}

/* ── 149: where it was taken (photo room admins: photo.location) ──────────── */

export type RoomLocation = {
  latitude: number
  longitude: number
  countryCode: string | null
  country: string | null
  region: string | null
  locality: string | null
  site: string | null
  siteConfidence: number | null
  siteStatus: 'suggested' | 'accepted' | 'rejected' | null
  placedAt: string | null
}

export async function photoLocations(paths: string[]): Promise<Map<string, RoomLocation>> {
  const out = new Map<string, RoomLocation>()
  if (paths.length === 0) return out
  const { data, error } = await supabase.rpc('photo_locations', { p_paths: paths })
  if (error) throw error
  for (const r of (data ?? []) as Record<string, unknown>[]) {
    out.set(r.storage_path as string, {
      latitude: Number(r.latitude),
      longitude: Number(r.longitude),
      countryCode: (r.country_code as string | null) ?? null,
      country: (r.country as string | null) ?? null,
      region: (r.region as string | null) ?? null,
      locality: (r.locality as string | null) ?? null,
      site: (r.site as string | null) ?? null,
      siteConfidence: r.site_confidence == null ? null : Number(r.site_confidence),
      siteStatus: (r.site_status as RoomLocation['siteStatus']) ?? null,
      placedAt: (r.placed_at as string | null) ?? null,
    })
  }
  return out
}

/* ── 321 (FED-74): the site — Gallery document users and admins ───────────── */

/** A photo's site: One's guess with its confidence, or what a person said. */
export type PhotoSite = {
  path: string
  siteId: string | null
  site: string | null
  confidence: number | null
  status: 'suggested' | 'accepted' | 'rejected' | null
  /** Who accepted or rejected it. */
  decidedBy: string | null
  /** One has looked at this photo the 321 way (seen it, and the sites near it). */
  oneHasLooked: boolean
}

/** Only photos with a location in the caller's Gallery come back (photo.room). */
export async function photoSitesFor(paths: string[]): Promise<Map<string, PhotoSite>> {
  const out = new Map<string, PhotoSite>()
  if (paths.length === 0) return out
  const { data, error } = await supabase.rpc('photo_sites_for', { p_paths: paths })
  if (error) throw error
  for (const r of (data ?? []) as Record<string, unknown>[]) out.set(String(r.storage_path), toPhotoSite(r))
  return out
}

export function toPhotoSite(r: Record<string, unknown>): PhotoSite {
  const st = r.site_status
  return {
    path: String(r.storage_path),
    siteId: typeof r.site_id === 'string' ? r.site_id : null,
    site: typeof r.site === 'string' && r.site.trim() ? r.site : null,
    confidence: r.site_confidence == null || r.site_confidence === '' ? null : Number(r.site_confidence),
    status: st === 'suggested' || st === 'accepted' || st === 'rejected' ? st : null,
    decidedBy: typeof r.decided_by_name === 'string' ? r.decided_by_name : null,
    oneHasLooked: r.one_has_looked === true,
  }
}

/** A site on the list: confirmed on at least one photo, not retired or merged. */
export type SiteOption = { id: string; label: string; tenantId: string; photos: number; hint?: string | null }

export async function siteOptions(): Promise<SiteOption[]> {
  const { data, error } = await supabase.rpc('photo_site_options')
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    id: String(r.site_id),
    label: String(r.name),
    tenantId: String(r.tenant_id),
    photos: Number(r.photos) || 0,
  }))
}

/**
 * Say what the site is: accept One's guess (no options), pick one from the
 * list (siteId), type a new one (newName - it joins the list), or say there
 * is none (accept false). A person's answer stands against One.
 */
export async function setPhotoSite(
  path: string,
  accept: boolean,
  opts: { siteId?: string | null; newName?: string | null } = {},
): Promise<string | null> {
  const { data, error } = await supabase.rpc('set_photo_site', {
    p_storage_path: path,
    p_accept: accept,
    p_site_id: opts.siteId ?? null,
    p_new_name: opts.newName ?? null,
  })
  if (error) throw error
  return typeof data === 'string' ? data : null
}

/** Every site name, for a Gallery admin keeping the list (photo.location). */
export type AdminSite = {
  id: string
  tenantId: string
  name: string
  /** A person has confirmed it on a photo; until then it is One's proposal, offered to nobody. */
  listed: boolean
  retired: boolean
  accepted: number
  suggested: number
}

export async function adminSites(): Promise<AdminSite[]> {
  const { data, error } = await supabase.rpc('photo_site_admin_list')
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    id: String(r.site_id),
    tenantId: String(r.tenant_id),
    name: String(r.name),
    listed: r.listed === true,
    retired: r.retired === true,
    accepted: Number(r.accepted) || 0,
    suggested: Number(r.suggested) || 0,
  }))
}

export async function renameSite(id: string, name: string): Promise<void> {
  const { error } = await supabase.rpc('rename_photo_site', { p_site_id: id, p_name: name })
  if (error) throw error
}

/** Every photo of `from` moves to `into`; `from` is gone from the list. */
export async function mergeSite(from: string, into: string): Promise<void> {
  const { error } = await supabase.rpc('merge_photo_site', { p_from: from, p_into: into })
  if (error) throw error
}

/** Off the list; One's guesses of it go, a person's confirmed answers stay. */
export async function retireSite(id: string): Promise<void> {
  const { error } = await supabase.rpc('retire_photo_site', { p_site_id: id })
  if (error) throw error
}

/** "Antofagasta, Antofagasta Region, Chile" — the place in plain words, or null. */
export function placeLine<T extends Pick<RoomLocation, 'locality' | 'region' | 'country'>>(l: T | null | undefined): string | null {
  if (!l) return null
  const parts = [l.locality, l.region, l.country].filter((x): x is string => !!x)
  const seen = new Set<string>()
  const uniq = parts.filter((x) => (seen.has(x.toLowerCase()) ? false : (seen.add(x.toLowerCase()), true)))
  return uniq.length ? uniq.join(', ') : null
}

/** supabase-js wraps a non-2xx in a FunctionsHttpError whose body holds the real message. */
async function readFunctionError(error: unknown): Promise<Error> {
  const ctx = (error as { context?: Response }).context
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = (await ctx.json()) as { error?: string }
      if (body?.error) return new Error(body.error)
    } catch {
      /* fall through */
    }
  }
  return error instanceof Error ? error : new Error('OneConetec could not answer just now')
}

/* ── vocabulary, tags, description ────────────────────────────────────────── */

export async function loadVocabulary(): Promise<VocabValue[]> {
  const { data, error } = await supabase.rpc('photo_vocabulary')
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    id: r.value_id as string,
    tenantId: r.tenant_id as string,
    facet: r.facet as Facet,
    key: r.key as string,
    labelEn: r.label_en as string,
    labelEs: r.label_es as string,
    retired: r.retired === true,
  }))
}

/** Yes (accepted) or no (rejected). A no stands: OneConetec never suggests it again. */
export async function setPhotoFeature(path: string, valueId: string, on: boolean): Promise<void> {
  const { error } = await supabase.rpc('set_photo_feature', { p_storage_path: path, p_value_id: valueId, p_on: on })
  if (error) throw error
}

/** A person's description. Both empty hands it back to OneConetec. */
export async function setPhotoDescription(path: string, en: string, es: string): Promise<void> {
  const { error } = await supabase.rpc('set_photo_description', { p_storage_path: path, p_en: en, p_es: es })
  if (error) throw error
}

export async function addVocabValue(facet: Facet, labelEn: string, labelEs: string): Promise<string> {
  const { data, error } = await supabase.rpc('add_photo_facet_value', {
    p_facet: facet,
    p_label_en: labelEn,
    p_label_es: labelEs,
  })
  if (error) throw error
  return data as string
}

/* ── 302 (FED-70): One reads a photo when it is posted, and learns ───────── */

/** One's tags on one of your own photos in the Gallery. */
export type MyPhotoTags = { described: boolean; features: RoomFeature[] }

/**
 * Your own Gallery photos among these paths: whether One has read each yet,
 * and the tags nobody has said no to. Anybody else's photo is simply not in
 * the map - the viewer asks for every photo on a post.
 */
export async function myPhotoTags(paths: string[]): Promise<Map<string, MyPhotoTags>> {
  const out = new Map<string, MyPhotoTags>()
  if (paths.length === 0) return out
  const { data, error } = await supabase.rpc('my_photo_features', { p_paths: paths })
  if (error) throw error
  for (const r of (data ?? []) as { storage_path: string; described: boolean; features: Record<string, unknown>[] | null }[]) {
    out.set(r.storage_path, {
      described: r.described === true,
      features: (r.features ?? []).map((f) => ({
        valueId: f.value_id as string,
        facet: f.facet as Facet,
        key: f.key as string,
        labelEn: f.label_en as string,
        labelEs: f.label_es as string,
        state: f.state === 'accepted' ? 'accepted' : 'suggested',
        confidence: typeof f.confidence === 'number' ? f.confidence : f.confidence == null ? null : Number(f.confidence),
      })),
    })
  }
  return out
}

/** The tag list a poster answers from: labels only, no recognition notes. */
export async function loadTagOptions(): Promise<VocabValue[]> {
  const { data, error } = await supabase.rpc('photo_tag_options')
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    id: r.value_id as string,
    tenantId: r.tenant_id as string,
    facet: r.facet as Facet,
    key: r.key as string,
    labelEn: r.label_en as string,
    labelEs: r.label_es as string,
    retired: false,
  }))
}

/** One tag as One is told about it (photo_describe_guide). Counts are photos, never people. */
export type TagGuideRow = {
  valueId: string
  facet: Facet
  key: string
  labelEn: string
  note: string | null
  oneSuggested: number
  peopleConfirmed: number
  peopleRejected: number
  peopleAdded: number
  /** Brand tags only: how many confirmed photos One is shown as references. */
  references: number
}

export async function loadTagGuide(tenantId: string): Promise<TagGuideRow[]> {
  const { data, error } = await supabase.rpc('photo_describe_guide', { p_tenant: tenantId })
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    valueId: r.value_id as string,
    facet: r.facet as Facet,
    key: r.key as string,
    labelEn: r.label_en as string,
    note: (r.cue as string | null) ?? null,
    oneSuggested: Number(r.one_suggested) || 0,
    peopleConfirmed: Number(r.people_confirmed) || 0,
    peopleRejected: Number(r.people_rejected) || 0,
    peopleAdded: Number(r.people_added) || 0,
    references: Array.isArray(r.example_paths) ? r.example_paths.length : 0,
  }))
}

/** A Gallery admin's recognition note. Empty clears it. */
export async function setTagNote(valueId: string, note: string): Promise<void> {
  const { error } = await supabase.rpc('set_photo_facet_cue', { p_value_id: valueId, p_cue: note })
  if (error) throw error
}

/* ── downloads ────────────────────────────────────────────────────────────── */

/** What record_photo_download said about the stars. The download is recorded in every case. */
export type DownloadOutcome = 'awarded' | 'duplicate' | 'own_photo' | 'byline' | 'capped' | 'rule_off' | 'not_member' | 'failed'
export type DownloadVariant = 'feed' | 'original'

/* 148: a download says what it is for, and the server refuses a use the
   poster opted the photo out of. There is no default use on purpose. */
export async function recordPhotoDownload(
  path: string,
  variant: DownloadVariant,
  use: PhotoUse,
): Promise<{ outcome: DownloadOutcome; stars: number }> {
  const { data, error } = await supabase.rpc('record_photo_download', {
    p_storage_path: path,
    p_variant: variant,
    p_use: use,
  })
  if (error) throw error
  const row = ((data ?? []) as { outcome: DownloadOutcome; stars: number }[])[0]
  return { outcome: row?.outcome ?? 'failed', stars: Number(row?.stars ?? 0) }
}

/**
 * A short-lived signed URL that the browser saves rather than opens
 * (Content-Disposition: attachment, under `fileName`). One minute: it is used
 * at once and must not be a link worth pasting anywhere.
 */
export async function signedDownloadUrl(path: string, fileName: string, variant: DownloadVariant = 'feed'): Promise<string> {
  const bucket = variant === 'original' ? 'photo-originals' : 'post-media'
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 60, { download: fileName })
  if (error || !data?.signedUrl) throw error ?? new Error('the photo could not be fetched')
  return data.signedUrl
}

/** A viewable (not downloading) URL for the full-size copy, for the viewer's zoom. */
export async function signedOriginalUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from('photo-originals').createSignedUrl(path, 600)
  return error || !data?.signedUrl ? null : data.signedUrl
}

/** Record, then fetch. Throws before any file is fetched if the record is refused. */
export async function downloadPhoto(
  photo: Pick<RoomPhoto, 'path' | 'uploaderName' | 'postedAt' | 'mimeType'>,
  variant: DownloadVariant,
  use: PhotoUse,
): Promise<{ outcome: DownloadOutcome; stars: number; fileSaved: boolean }> {
  const result = await recordPhotoDownload(photo.path, variant, use)
  try {
    const url = await signedDownloadUrl(photo.path, roomFileName(photo, variant), variant)
    const a = document.createElement('a')
    a.href = url
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
    return { ...result, fileSaved: true }
  } catch {
    // Recorded but the file did not come down. Trying again records a second
    // download and pays nothing more.
    return { ...result, fileSaved: false }
  }
}

/** "oneconetec-jane-doe-2026-10-09-3f9a1c.jpg" — who, when, and enough of the path to tell two apart. */
export function roomFileName(
  photo: Pick<RoomPhoto, 'path' | 'uploaderName' | 'postedAt' | 'mimeType'>,
  variant: DownloadVariant = 'feed',
): string {
  const who =
    photo.uploaderName
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'photo'
  const day = (photo.postedAt ?? '').slice(0, 10)
  const file = photo.path.split('/').pop() ?? ''
  const fromPath = file.includes('.') ? file.split('.').pop()!.toLowerCase() : ''
  const ext = /^[a-z0-9]{2,5}$/.test(fromPath) ? fromPath : extensionFor(photo.mimeType)
  const tail = file.replace(/\.[^.]*$/, '').replace(/[^a-z0-9]/gi, '').slice(-6).toLowerCase()
  return ['oneconetec', who, day, tail, variant === 'original' ? 'full' : ''].filter(Boolean).join('-') + '.' + ext
}

function extensionFor(mime: string): string {
  if (mime === 'image/png') return 'png'
  if (mime === 'image/webp') return 'webp'
  if (mime === 'image/heic') return 'heic'
  return 'jpg'
}

/* ── what the metadata panel derives (rule 8: derived, never stored) ──────── */

/** Inches at 300 dpi, one decimal: what a designer asks first. */
export function printInches(width: number, height: number, dpi = 300): { w: number; h: number } {
  return { w: Math.round((width / dpi) * 10) / 10, h: Math.round((height / dpi) * 10) / 10 }
}

export function orientationOf(width: number | null, height: number | null): 'landscape' | 'portrait' | 'square' | null {
  if (!width || !height) return null
  const r = width / height
  if (r > 1.05) return 'landscape'
  if (r < 0.95) return 'portrait'
  return 'square'
}

export function megabytes(bytes: number | null): string | null {
  if (!bytes) return null
  return (bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0) + ' MB'
}

/**
 * What `photo.used` pays now, from the member-readable rule (142's pattern for
 * the composer's +50). Null when the rule is off — the default since 146 — and
 * the page then names no number.
 */
export async function photoUsedReward(tenantId: string): Promise<number | null> {
  const { data, error } = await supabase
    .from('star_earning_rule')
    .select('stars, is_enabled')
    .eq('tenant_id', tenantId)
    .eq('rule_key', 'photo.used')
    .maybeSingle()
  if (error || !data || !data.is_enabled || !(data.stars > 0)) return null
  return data.stars as number
}

/** The default first-place stars for a new contest (photo.contest_win's rate). */
export async function contestWinDefault(tenantId: string): Promise<number> {
  const { data } = await supabase
    .from('star_earning_rule')
    .select('stars, is_enabled')
    .eq('tenant_id', tenantId)
    .eq('rule_key', 'photo.contest_win')
    .maybeSingle()
  return data && data.is_enabled ? Number(data.stars) || 0 : 0
}

/* ── contests ─────────────────────────────────────────────────────────────── */

export type ContestPlace = { place: number; stars: number }
export type ContestWinner = { place: number; path: string; contentId: string | null; personId: string; name: string }
export type Contest = {
  id: string
  tenantId: string
  title: string
  startsOn: string
  endsOn: string
  timezone: string
  places: ContestPlace[]
  winners: ContestWinner[]
  entryCount: number
  createdByName: string
  judgedAt: string | null
  judgedByName: string | null
  cancelledAt: string | null
}

export async function loadContests(): Promise<Contest[]> {
  const { data, error } = await supabase.rpc('photo_contests')
  if (error) throw error
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    id: r.contest_id as string,
    tenantId: r.tenant_id as string,
    title: r.title as string,
    startsOn: r.starts_on as string,
    endsOn: r.ends_on as string,
    timezone: r.timezone as string,
    places: (r.places as ContestPlace[] | null) ?? [],
    winners: ((r.winners as { place: number; storage_path: string; content_id: string | null; person_id: string; name: string }[] | null) ?? []).map(
      (w) => ({ place: w.place, path: w.storage_path, contentId: w.content_id, personId: w.person_id, name: w.name }),
    ),
    entryCount: Number(r.entry_count ?? 0),
    createdByName: r.created_by_name as string,
    judgedAt: (r.judged_at as string | null) ?? null,
    judgedByName: (r.judged_by_name as string | null) ?? null,
    cancelledAt: (r.cancelled_at as string | null) ?? null,
  }))
}

/** Create (id null) or replace a contest that is not judged or cancelled. Every value, every time (rule 9). */
export async function saveContest(c: {
  id: string | null
  title: string
  startsOn: string
  endsOn: string
  timezone: string
  placeStars: number[]
}): Promise<string> {
  const { data, error } = await supabase.rpc('save_photo_contest', {
    p_id: c.id,
    p_title: c.title,
    p_starts_on: c.startsOn,
    p_ends_on: c.endsOn,
    p_timezone: c.timezone,
    p_place_stars: c.placeStars,
  })
  if (error) throw error
  return data as string
}

export async function cancelContest(id: string): Promise<void> {
  const { error } = await supabase.rpc('cancel_photo_contest', { p_id: id })
  if (error) throw error
}

export type AwardOutcome = {
  place: number
  path: string
  personId: string
  outcome: 'awarded' | 'rule_off' | 'capped' | 'not_member' | 'duplicate' | 'nothing' | 'failed'
  stars: number
  told: boolean
}

/** FINAL: records the winners, pays each, rings each bell. */
export async function awardContest(id: string, winners: { place: number; path: string }[]): Promise<AwardOutcome[]> {
  const { data, error } = await supabase.rpc('award_photo_contest', {
    p_contest_id: id,
    p_winners: winners.map((w) => ({ place: w.place, storage_path: w.path })),
  })
  if (error) throw error
  return ((data ?? []) as { place: number; storage_path: string; person_id: string; outcome: AwardOutcome['outcome']; stars: number; told: boolean }[]).map(
    (r) => ({ place: r.place, path: r.storage_path, personId: r.person_id, outcome: r.outcome, stars: Number(r.stars) || 0, told: r.told }),
  )
}

/** The first and last day of the month a date falls in, as YYYY-MM-DD. */
export function monthRange(d: Date): { from: string; to: string } {
  const y = d.getFullYear()
  const m = d.getMonth()
  const pad = (n: number) => String(n).padStart(2, '0')
  const last = new Date(y, m + 1, 0).getDate()
  return { from: `${y}-${pad(m + 1)}-01`, to: `${y}-${pad(m + 1)}-${pad(last)}` }
}
